// ============================================================
//  BootScene — loads production art assets, creates procedural
//              textures, then launches Hub
// ============================================================
import { C } from '../../constants/Colors.js';
import { NUMBER_KINGDOM_ASSET_LIST } from '../worlds/number-kingdom/NumberKingdomAssets.js';
import {
  getMissingHomeHubAssets,
  HOME_HUB_ASSET_LIST,
  preloadHomeHubAssets,
} from './HomeHubAssets.js';
import {
  DISCOVERY_LAB_RUNTIME_ASSET_LIST,
  getMissingDiscoveryLabRuntimeAssets,
  preloadDiscoveryLabAssets,
} from '../worlds/discovery-lab/DiscoveryLabAssets.js';
import {
  ADDITION_CITADEL_IMAGE_ASSETS,
  getUnloadedAdditionCitadelImages,
} from '../worlds/number-kingdom/districts/addition-citadel/AdditionCitadelAssets.js';
import {
  getMissingStoryForestAssets,
  preloadStoryForestAssets,
  STORY_FOREST_ASSET_LIST,
} from '../worlds/story-forest/StoryForestAssets.js';
import {
  getMissingWordGardenAssets,
  preloadWordGardenAssets,
  WORD_GARDEN_ASSET_LIST,
} from '../worlds/story-forest/minigames/word-garden/WordGardenAssets.js';
import {
  getMissingRegroupingWorkshopImages,
  preloadRegroupingWorkshopAssets,
  REGROUPING_WORKSHOP_ASSET_LIST,
} from '../worlds/number-kingdom/districts/addition-citadel/regrouping-workshop/RegroupingWorkshopAssets.js';
import {
  CHARACTER_GROVE_ASSET_LIST,
  getMissingCharacterGroveAssets,
  preloadCharacterGroveAssets,
} from '../worlds/story-forest/minigames/character-grove/CharacterGroveAssets.js';
import {
  getMissingMaterialsWorkshopAssets,
  MATERIALS_WORKSHOP_ASSET_REQUIREMENTS,
  preloadMaterialsWorkshopAssets,
} from '../worlds/discovery-lab/materials-workshop/MaterialsWorkshopAssets.js';
import {
  getMissingSubtractionKeepAssets,
  preloadSubtractionKeepAssets,
  SUBTRACTION_KEEP_ASSET_LIST,
} from '../worlds/number-kingdom/districts/subtraction-keep/SubtractionKeepAssets.js';
import {
  getMissingMeasurementMarketAssets,
  MEASUREMENT_MARKET_ASSET_LIST,
  preloadMeasurementMarketAssets,
} from '../worlds/number-kingdom/districts/measurement-market/MeasurementMarketAssets.js';
import {
  CLOCKTOWER_PLAZA_ASSET_LIST,
  getMissingClocktowerPlazaAssets,
  preloadClocktowerPlazaAssets,
} from '../worlds/number-kingdom/districts/clocktower-plaza/ClocktowerPlazaAssets.js';
import {
  COINMAKERS_VAULT_ASSET_LIST,
  getMissingCoinmakersVaultAssets,
  preloadCoinmakersVaultAssets,
} from '../worlds/number-kingdom/districts/coinmakers-vault/CoinmakersVaultAssets.js';
import {
  getMissingRoyalChallengeHallAssets,
  preloadRoyalChallengeHallAssets,
  ROYAL_CHALLENGE_HALL_ASSET_LIST,
} from '../worlds/number-kingdom/districts/royal-challenge-hall/RoyalChallengeHallAssets.js';
import {
  getMissingSequenceStreamAssets,
  preloadSequenceStreamAssets,
  SEQUENCE_STREAM_ASSET_LIST,
} from '../worlds/story-forest/sequence-stream/SequenceStreamAssets.js';
import {
  getMissingMainIdeaMeadowAssets,
  MAIN_IDEA_MEADOW_ASSET_LIST,
  preloadMainIdeaMeadowAssets,
} from '../worlds/story-forest/main-idea-meadow/MainIdeaMeadowAssets.js';
import {
  getMissingInferenceHollowAssets,
  INFERENCE_HOLLOW_ASSET_LIST,
  preloadInferenceHollowAssets,
} from '../worlds/story-forest/inference-hollow/InferenceHollowAssets.js';
import {
  AUTHORS_CABIN_ASSET_LIST,
  getMissingAuthorsCabinAssets,
  preloadAuthorsCabinAssets,
} from '../worlds/story-forest/authors-cabin/AuthorsCabinAssets.js';
import {
  getMissingGrammarGladeAssets,
  GRAMMAR_GLADE_ASSET_LIST,
  preloadGrammarGladeAssets,
} from '../worlds/story-forest/grammar-glade/GrammarGladeAssets.js';
import {
  FLUENCY_LANTERN_ASSET_LIST,
  getMissingFluencyLanternAssets,
  preloadFluencyLanternAssets,
} from '../worlds/story-forest/fluency-lantern-path/FluencyLanternAssets.js';
import {
  getMissingGreatStoryTreeAssets,
  GREAT_STORY_TREE_ASSET_LIST,
  preloadGreatStoryTreeAssets,
} from '../worlds/story-forest/great-story-tree/GreatStoryTreeAssets.js';
import {
  getMissingWeatherWatchAssets,
  preloadWeatherWatchAssets,
  WEATHER_WATCH_ASSET_LIST,
} from '../worlds/discovery-lab/weather-watch-station/WeatherWatchAssets.js';
import { getMissingHabitatFieldAssets, HABITAT_FIELD_ASSET_LIST, preloadHabitatFieldAssets } from '../worlds/discovery-lab/habitat-field-camp/HabitatFieldAssets.js';
import {
  getMissingPlantGrowthAssets,
  PLANT_GROWTH_ASSET_LIST,
  preloadPlantGrowthAssets,
} from '../worlds/discovery-lab/plant-growth-house/PlantGrowthAssets.js';
import {
  getMissingLifeCycleAssets,
  LIFE_CYCLE_ASSET_LIST,
  preloadLifeCycleAssets,
} from '../worlds/discovery-lab/life-cycle-pond/LifeCycleAssets.js';
import {
  getMissingSoundVibrationAssets,
  SOUND_VIBRATION_ASSET_LIST,
  preloadSoundVibrationAssets,
} from '../worlds/discovery-lab/sound-vibration-deck/SoundVibrationAssets.js';
import {
  getMissingEarthMaterialsAssets,
  EARTH_MATERIALS_ASSET_LIST,
  preloadEarthMaterialsAssets,
} from '../worlds/discovery-lab/earth-materials-yard/EarthMaterialsAssets.js';
import {
  getMissingDiscoveryObservatoryAssets,
  DISCOVERY_OBSERVATORY_ASSET_LIST,
  preloadDiscoveryObservatoryAssets,
} from '../worlds/discovery-lab/discovery-observatory/DiscoveryObservatoryAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  type NumberKingdomDistrict,
} from '../worlds/number-kingdom/districts/NumberKingdomDistrictRegistry.js';
import { visitNumberKingdomDistrict } from '../worlds/number-kingdom/NumberKingdomState.js';
import {
  STORY_FOREST_DISTRICTS,
  type StoryForestDistrict,
  visitStoryForestDistrict,
} from '../worlds/story-forest/StoryForestState.js';
import {
  DISCOVERY_LAB_DISTRICTS,
  type DiscoveryLabDistrict,
} from '../worlds/discovery-lab/DiscoveryLabConfig.js';
import { visitDiscoveryLabDistrict } from '../worlds/discovery-lab/DiscoveryLabState.js';

interface DevelopmentQaRoute {
  readonly scene: string;
  readonly unlock: () => void;
}

const developmentQaRoute = (
  scene: string,
  district: NumberKingdomDistrict | StoryForestDistrict | DiscoveryLabDistrict,
  visit: (value: never) => unknown,
): DevelopmentQaRoute => ({
  scene,
  unlock: () => { visit(district as never); },
});

const DEVELOPMENT_QA_ROUTES: readonly DevelopmentQaRoute[] = [
  developmentQaRoute(
    'MeasurementMarket',
    NUMBER_KINGDOM_DISTRICTS.measureworks,
    visitNumberKingdomDistrict,
  ),
  developmentQaRoute(
    'ClocktowerPlaza',
    NUMBER_KINGDOM_DISTRICTS.clockTower,
    visitNumberKingdomDistrict,
  ),
  developmentQaRoute(
    'CoinmakersVault',
    NUMBER_KINGDOM_DISTRICTS.royalMarket,
    visitNumberKingdomDistrict,
  ),
  developmentQaRoute(
    'RoyalChallengeHall',
    NUMBER_KINGDOM_DISTRICTS.masteryKeep,
    visitNumberKingdomDistrict,
  ),
  developmentQaRoute('SequenceStream', STORY_FOREST_DISTRICTS.sequenceStream, visitStoryForestDistrict),
  developmentQaRoute('MainIdeaMeadow', STORY_FOREST_DISTRICTS.mainIdeaMeadow, visitStoryForestDistrict),
  developmentQaRoute('InferenceHollow', STORY_FOREST_DISTRICTS.inferenceHollow, visitStoryForestDistrict),
  developmentQaRoute('AuthorsCabin', STORY_FOREST_DISTRICTS.authorsCabin, visitStoryForestDistrict),
  developmentQaRoute('GrammarGlade', STORY_FOREST_DISTRICTS.grammarGlade, visitStoryForestDistrict),
  developmentQaRoute(
    'FluencyLanternPath',
    STORY_FOREST_DISTRICTS.fluencyLanternPath,
    visitStoryForestDistrict,
  ),
  developmentQaRoute('GreatStoryTree', STORY_FOREST_DISTRICTS.greatStoryTree, visitStoryForestDistrict),
  developmentQaRoute(
    'WeatherWatchStation',
    DISCOVERY_LAB_DISTRICTS.weatherWatchStation,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'HabitatFieldCamp',
    DISCOVERY_LAB_DISTRICTS.habitatFieldCamp,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'PlantGrowthHouse',
    DISCOVERY_LAB_DISTRICTS.plantGrowthHouse,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'LifeCyclePond',
    DISCOVERY_LAB_DISTRICTS.lifeCyclePond,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'SoundVibrationDeck',
    DISCOVERY_LAB_DISTRICTS.soundVibrationDeck,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'EarthMaterialsYard',
    DISCOVERY_LAB_DISTRICTS.earthMaterialsYard,
    visitDiscoveryLabDistrict,
  ),
  developmentQaRoute(
    'DiscoveryObservatory',
    DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
    visitDiscoveryLabDistrict,
  ),
];

export class BootScene extends Phaser.Scene {
  constructor() { super({ key: 'Boot' }); }

  preload() {
    // Production art assets
    preloadHomeHubAssets(this);
    NUMBER_KINGDOM_ASSET_LIST.forEach(asset => this.load.image(asset.key, asset.path));
    ADDITION_CITADEL_IMAGE_ASSETS.forEach(asset => this.load.image(asset.key, asset.path));
    preloadDiscoveryLabAssets(this);
    preloadStoryForestAssets(this);
    preloadWordGardenAssets(this);
    preloadRegroupingWorkshopAssets(this);
    preloadCharacterGroveAssets(this);
    preloadMaterialsWorkshopAssets(this);
    preloadSubtractionKeepAssets(this);
    preloadMeasurementMarketAssets(this);
    preloadClocktowerPlazaAssets(this);
    preloadCoinmakersVaultAssets(this);
    preloadRoyalChallengeHallAssets(this);
    preloadSequenceStreamAssets(this);
    preloadMainIdeaMeadowAssets(this);
    preloadInferenceHollowAssets(this);
    preloadAuthorsCabinAssets(this);
    preloadGrammarGladeAssets(this);
    preloadFluencyLanternAssets(this);
    preloadGreatStoryTreeAssets(this);
    preloadWeatherWatchAssets(this);
    preloadHabitatFieldAssets(this);
    preloadPlantGrowthAssets(this);
    preloadLifeCycleAssets(this);
    preloadSoundVibrationAssets(this);
    preloadEarthMaterialsAssets(this);
    preloadDiscoveryObservatoryAssets(this);
  }

  create() {
    const missingHomeHubAssets = getMissingHomeHubAssets(this.textures);
    if (missingHomeHubAssets.length > 0) {
      console.error(
        '[Boot] Home Hub production textures failed to load:',
        missingHomeHubAssets.map(asset => `${asset.key} (${asset.path})`),
      );
      return;
    }

    const missingAdditionCitadelAssets = getUnloadedAdditionCitadelImages(this.textures);
    if (missingAdditionCitadelAssets.length > 0) {
      console.error(
        '[Boot] Addition Citadel production textures failed to load:',
        missingAdditionCitadelAssets.map(asset => `${asset.key} (${asset.path})`),
      );
      return;
    }

    const missingDiscoveryLabAssets = getMissingDiscoveryLabRuntimeAssets(this.textures);
    if (missingDiscoveryLabAssets.length > 0) {
      console.error(
        '[Boot] Discovery Lab production textures failed to load:',
        missingDiscoveryLabAssets.map(asset => `${asset.key} (${asset.path})`),
      );
      return;
    }

    const missingNewWorldAssets = [
      ...getMissingRegroupingWorkshopImages(this.textures),
      ...getMissingStoryForestAssets(this),
      ...getMissingWordGardenAssets(this),
      ...getMissingCharacterGroveAssets(this.textures),
      ...getMissingMaterialsWorkshopAssets(this.textures),
      ...getMissingSubtractionKeepAssets(this.textures),
      ...getMissingMeasurementMarketAssets(this.textures),
      ...getMissingClocktowerPlazaAssets(this.textures),
      ...getMissingCoinmakersVaultAssets(this.textures),
      ...getMissingRoyalChallengeHallAssets(this.textures),
      ...getMissingSequenceStreamAssets(this.textures),
      ...getMissingMainIdeaMeadowAssets(this.textures),
      ...getMissingInferenceHollowAssets(this.textures),
      ...getMissingAuthorsCabinAssets(this.textures),
      ...getMissingGrammarGladeAssets(this.textures),
      ...getMissingFluencyLanternAssets(this.textures),
      ...getMissingGreatStoryTreeAssets(this.textures),
      ...getMissingWeatherWatchAssets(this.textures),
      ...getMissingHabitatFieldAssets(this.textures),
      ...getMissingPlantGrowthAssets(this.textures),
      ...getMissingLifeCycleAssets(this.textures),
      ...getMissingSoundVibrationAssets(this.textures),
      ...getMissingEarthMaterialsAssets(this.textures),
      ...getMissingDiscoveryObservatoryAssets(this.textures),
    ];
    if (missingNewWorldAssets.length > 0) {
      console.error(
        '[Boot] Production route textures failed to load:',
        missingNewWorldAssets.map(asset => `${asset.key} (${asset.path})`),
      );
      return;
    }

    const runtimeImageKeys = [
      ...HOME_HUB_ASSET_LIST.map(asset => asset.key),
      ...NUMBER_KINGDOM_ASSET_LIST.map(asset => asset.key),
      ...ADDITION_CITADEL_IMAGE_ASSETS.map(asset => asset.key),
      ...DISCOVERY_LAB_RUNTIME_ASSET_LIST
        .filter(asset => asset.kind === 'image')
        .map(asset => asset.key),
    ];
    const registeredRuntimeImageKeys = [
      ...runtimeImageKeys,
      ...STORY_FOREST_ASSET_LIST.map(asset => asset.key),
      ...WORD_GARDEN_ASSET_LIST.map(asset => asset.key),
      ...REGROUPING_WORKSHOP_ASSET_LIST.map(asset => asset.key),
      ...CHARACTER_GROVE_ASSET_LIST.map(asset => asset.key),
      ...MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.map(asset => asset.key),
      ...SUBTRACTION_KEEP_ASSET_LIST.map(asset => asset.key),
      ...MEASUREMENT_MARKET_ASSET_LIST.map(asset => asset.key),
      ...CLOCKTOWER_PLAZA_ASSET_LIST.map(asset => asset.key),
      ...COINMAKERS_VAULT_ASSET_LIST.map(asset => asset.key),
      ...ROYAL_CHALLENGE_HALL_ASSET_LIST.map(asset => asset.key),
      ...SEQUENCE_STREAM_ASSET_LIST.map(asset => asset.key),
      ...MAIN_IDEA_MEADOW_ASSET_LIST.map(asset => asset.key),
      ...INFERENCE_HOLLOW_ASSET_LIST.map(asset => asset.key),
      ...AUTHORS_CABIN_ASSET_LIST.map(asset => asset.key),
      ...GRAMMAR_GLADE_ASSET_LIST.map(asset => asset.key),
      ...FLUENCY_LANTERN_ASSET_LIST.map(asset => asset.key),
      ...GREAT_STORY_TREE_ASSET_LIST.map(asset => asset.key),
      ...WEATHER_WATCH_ASSET_LIST.map(asset => asset.key),
      ...HABITAT_FIELD_ASSET_LIST.map(asset => asset.key),
      ...PLANT_GROWTH_ASSET_LIST.map(asset => asset.key),
      ...LIFE_CYCLE_ASSET_LIST.map(asset => asset.key),
      ...SOUND_VIBRATION_ASSET_LIST.map(asset => asset.key),
      ...EARTH_MATERIALS_ASSET_LIST.map(asset => asset.key),
      ...DISCOVERY_OBSERVATORY_ASSET_LIST.map(asset => asset.key),
    ];
    const duplicateRuntimeImageKeys = registeredRuntimeImageKeys.filter(
      (key, index) => registeredRuntimeImageKeys.indexOf(key) !== index,
    );
    if (duplicateRuntimeImageKeys.length > 0) {
      console.error(
        '[Boot] Duplicate runtime texture keys detected:',
        [...new Set(duplicateRuntimeImageKeys)],
      );
      return;
    }

    runtimeImageKeys.forEach(key => {
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    });
    [
      ...REGROUPING_WORKSHOP_ASSET_LIST,
      ...CHARACTER_GROVE_ASSET_LIST,
      ...MATERIALS_WORKSHOP_ASSET_REQUIREMENTS,
      ...SUBTRACTION_KEEP_ASSET_LIST,
      ...MEASUREMENT_MARKET_ASSET_LIST,
      ...CLOCKTOWER_PLAZA_ASSET_LIST,
      ...COINMAKERS_VAULT_ASSET_LIST,
      ...ROYAL_CHALLENGE_HALL_ASSET_LIST,
      ...SEQUENCE_STREAM_ASSET_LIST,
      ...MAIN_IDEA_MEADOW_ASSET_LIST,
      ...INFERENCE_HOLLOW_ASSET_LIST,
      ...AUTHORS_CABIN_ASSET_LIST,
      ...GRAMMAR_GLADE_ASSET_LIST,
      ...FLUENCY_LANTERN_ASSET_LIST,
      ...GREAT_STORY_TREE_ASSET_LIST,
      ...WEATHER_WATCH_ASSET_LIST,
      ...HABITAT_FIELD_ASSET_LIST,
      ...PLANT_GROWTH_ASSET_LIST,
      ...LIFE_CYCLE_ASSET_LIST,
      ...SOUND_VIBRATION_ASSET_LIST,
      ...EARTH_MATERIALS_ASSET_LIST,
      ...DISCOVERY_OBSERVATORY_ASSET_LIST,
    ].forEach(asset => this.textures.get(asset.key).setFilter(Phaser.Textures.FilterMode.LINEAR));

    this.makeParticleTextures();
    this.makeUITextures();
    this.makeBlockTextures();
    this.makeBridgeTextures();
    this.makePlanetTextures();
    this.makeCrystalTextures();

    // Hide HTML boot screen
    const bootEl = document.getElementById('boot-screen');
    if (bootEl) {
      bootEl.classList.add('hidden');
      setTimeout(() => bootEl.remove(), 900);
    }

    if (import.meta.env.DEV) {
      const requestedScene = new URLSearchParams(window.location.search).get('qaScene');
      const qaRoute = DEVELOPMENT_QA_ROUTES.find(route => route.scene === requestedScene);
      if (qaRoute) {
        qaRoute.unlock();
        this.scene.start(qaRoute.scene);
        return;
      }
    }

    this.scene.start('HomeHub');
  }

  // ── Particle textures ────────────────────────────────────────
  private makeParticleTextures() {
    // White circle particle
    this.makeTexture('particle_white', 10, 10, (g) => {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0xffffff, 0.2 * i);
        g.fillCircle(5, 5, i + 2);
      }
      g.fillStyle(0xffffff, 1);
      g.fillCircle(5, 5, 2.5);
    });

    // Star particle
    this.makeTexture('particle_star', 16, 16, (g) => {
      g.fillStyle(0xffd700, 1);
      this.starPath(g, 8, 8, 5, 7, 3);
    });

    // Cyan spark
    this.makeTexture('particle_cyan', 8, 8, (g) => {
      g.fillStyle(0x00e5ff, 0.3); g.fillCircle(4, 4, 4);
      g.fillStyle(0x00e5ff, 1);   g.fillCircle(4, 4, 2);
    });

    // Gold orb
    this.makeTexture('particle_gold', 12, 12, (g) => {
      g.fillStyle(0xff8c00, 0.25); g.fillCircle(6, 6, 6);
      g.fillStyle(0xffd700, 0.6);  g.fillCircle(6, 6, 4);
      g.fillStyle(0xffffff, 0.9);  g.fillCircle(6, 6, 2);
    });

    // Purple nebula puff
    this.makeTexture('particle_nebula', 20, 20, (g) => {
      g.fillStyle(0x9b4dff, 0.15); g.fillCircle(10, 10, 10);
      g.fillStyle(0x7b2fff, 0.25); g.fillCircle(10, 10, 7);
      g.fillStyle(0xaa60ff, 0.4);  g.fillCircle(10, 10, 4);
    });
  }

  // ── UI textures ──────────────────────────────────────────────
  private makeUITextures() {
    this.makeTexture('btn_primary', 220, 56, (g) => {
      g.fillStyle(0xff8c00, 0.3); g.fillRoundedRect(-8, -8, 236, 72, 24);
      g.fillStyle(0xff6b00, 1);   g.fillRoundedRect(0, 0, 220, 56, 18);
      g.fillStyle(0xff8c00, 1);   g.fillRoundedRect(2, 2, 216, 28, 16);
      g.fillStyle(0xffa500, 0.6); g.fillRoundedRect(4, 4, 130, 18, 12);
    });

    this.makeTexture('btn_secondary', 180, 50, (g) => {
      g.fillStyle(0x3d1060, 1);   g.fillRoundedRect(0, 0, 180, 50, 16);
      g.fillStyle(0x5a1888, 1);   g.fillRoundedRect(2, 2, 176, 24, 14);
      g.lineStyle(1.5, 0x9b4dff, 0.8);
      g.strokeRoundedRect(0, 0, 180, 50, 16);
    });

    this.makeTexture('panel_dark', 400, 120, (g) => {
      g.fillStyle(0x0d0230, 0.92);  g.fillRoundedRect(0, 0, 400, 120, 20);
      g.lineStyle(1, 0x3d2060, 0.8); g.strokeRoundedRect(0, 0, 400, 120, 20);
    });

    this.makeTexture('portal_ring', 120, 120, (g) => {
      for (let i = 5; i >= 1; i--) {
        g.lineStyle(i * 3, 0x00e5ff, 0.06 * i);
        g.strokeCircle(60, 60, 48 + i * 4);
      }
      g.lineStyle(2.5, 0x00e5ff, 0.9);
      g.strokeCircle(60, 60, 48);
      g.lineStyle(1.5, 0xffffff, 0.5);
      g.strokeCircle(60, 60, 40);
    });
  }

  // ── Place-value block textures ───────────────────────────────
  private makeBlockTextures() {
    this.makeTexture('block_hundred', 80, 80, (g) => {
      for (let i = 4; i >= 1; i--) {
        g.fillStyle(0x9b4dff, 0.07 * i);
        g.fillRoundedRect(-i * 3, -i * 3, 80 + i * 6, 80 + i * 6, 10);
      }
      g.fillStyle(0x5a1888, 1);  g.fillRoundedRect(0, 0, 80, 80, 10);
      g.fillStyle(0x7b2fff, 1);  g.fillRoundedRect(3, 3, 74, 74, 8);
      g.fillStyle(0x9b4dff, 0.5); g.fillRoundedRect(6, 6, 60, 25, 6);
      g.fillStyle(0xffffff, 0.15); g.fillRoundedRect(8, 8, 30, 12, 4);
      g.lineStyle(1, 0xc080ff, 0.6); g.strokeRoundedRect(3, 3, 74, 74, 8);
    });

    this.makeTexture('block_ten', 28, 80, (g) => {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0x00e5ff, 0.07 * i);
        g.fillRoundedRect(-i * 2, -i * 2, 28 + i * 4, 80 + i * 4, 6);
      }
      g.fillStyle(0x007a99, 1);  g.fillRoundedRect(0, 0, 28, 80, 6);
      g.fillStyle(0x00bcd4, 1);  g.fillRoundedRect(2, 2, 24, 76, 5);
      g.fillStyle(0x00e5ff, 0.5); g.fillRoundedRect(4, 4, 16, 25, 4);
      g.fillStyle(0xffffff, 0.15); g.fillRoundedRect(5, 5, 8, 12, 3);
      g.lineStyle(1, 0x80ffff, 0.6); g.strokeRoundedRect(2, 2, 24, 76, 5);
    });

    this.makeTexture('block_one', 26, 26, (g) => {
      for (let i = 2; i >= 1; i--) {
        g.fillStyle(0xffd700, 0.1 * i);
        g.fillRoundedRect(-i * 2, -i * 2, 26 + i * 4, 26 + i * 4, 4);
      }
      g.fillStyle(0xcc8800, 1); g.fillRoundedRect(0, 0, 26, 26, 4);
      g.fillStyle(0xffc107, 1); g.fillRoundedRect(2, 2, 22, 22, 3);
      g.fillStyle(0xffd700, 0.6); g.fillRoundedRect(3, 3, 12, 8, 2);
      g.fillStyle(0xffffff, 0.2); g.fillRoundedRect(4, 4, 6, 4, 2);
      g.lineStyle(1, 0xffe082, 0.7); g.strokeRoundedRect(2, 2, 22, 22, 3);
    });
  }

  // ── Bridge textures ──────────────────────────────────────────
  private makeBridgeTextures() {
    this.makeTexture('bridge_dark', 160, 50, (g) => {
      g.fillStyle(0x1a0a2e, 1);   g.fillRoundedRect(0, 0, 160, 50, 8);
      g.fillStyle(0x2d1a4a, 1);   g.fillRoundedRect(3, 3, 154, 20, 6);
      g.lineStyle(1.5, 0x3d2060, 0.6); g.strokeRoundedRect(0, 0, 160, 50, 8);
      for (let x = 20; x < 150; x += 30) {
        g.lineStyle(1, 0x3d2060, 0.3);
        g.lineBetween(x, 5, x, 45);
      }
    });

    this.makeTexture('bridge_lit', 160, 50, (g) => {
      for (let i = 4; i >= 1; i--) {
        g.fillStyle(0xff8c00, 0.06 * i);
        g.fillRoundedRect(-i * 3, -i * 3, 160 + i * 6, 50 + i * 6, 10);
      }
      g.fillStyle(0x3d2000, 1);   g.fillRoundedRect(0, 0, 160, 50, 8);
      g.fillStyle(0xff4500, 1);   g.fillRoundedRect(3, 3, 154, 20, 6);
      g.fillStyle(0xff8c00, 0.6); g.fillRoundedRect(4, 4, 100, 10, 4);
      g.fillStyle(0xffd700, 0.3); g.fillRoundedRect(5, 5, 50, 5, 3);
      g.lineStyle(2, 0xffcc00, 0.9); g.strokeRoundedRect(0, 0, 160, 50, 8);
      for (let x = 20; x < 150; x += 30) {
        g.lineStyle(1.5, 0xff8c00, 0.5);
        g.lineBetween(x, 5, x, 45);
      }
    });
  }

  // ── Planet textures ──────────────────────────────────────────
  private makePlanetTextures() {
    this.makeTexture('planet_purple', 100, 100, (g) => {
      for (let i = 5; i >= 1; i--) {
        g.fillStyle(0x4a1080, 0.08 * i); g.fillCircle(50, 50, 50 + i * 8);
      }
      g.fillStyle(0x2d0060, 1); g.fillCircle(50, 50, 50);
      g.fillStyle(0x5a1a9a, 1); g.fillCircle(50, 50, 42);
      g.fillStyle(0x7b2fff, 0.5); g.fillCircle(44, 44, 28);
      g.fillStyle(0x9040ff, 0.3); g.fillCircle(56, 54, 18);
      g.fillStyle(0x6030b0, 0.4); g.fillEllipse(50, 36, 80, 12);
      g.fillStyle(0x3d1080, 0.3); g.fillEllipse(50, 62, 70, 10);
      g.fillStyle(0xffffff, 0.12); g.fillCircle(32, 30, 14);
    });

    this.makeTexture('planet_blue', 70, 70, (g) => {
      for (let i = 4; i >= 1; i--) {
        g.fillStyle(0x0020a0, 0.08 * i); g.fillCircle(35, 35, 35 + i * 6);
      }
      g.fillStyle(0x001060, 1); g.fillCircle(35, 35, 35);
      g.fillStyle(0x0050c0, 1); g.fillCircle(35, 35, 30);
      g.fillStyle(0x00a0ff, 0.4); g.fillCircle(30, 30, 18);
      g.fillStyle(0xffffff, 0.2); g.fillCircle(22, 22, 10);
    });
  }

  // ── Crystal textures ─────────────────────────────────────────
  private makeCrystalTextures() {
    this.makeTexture('crystal_purple', 30, 60, (g) => {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0x9b4dff, 0.08 * i); g.fillTriangle(15, -i * 4, -i * 3, 60, 30 + i * 3, 60);
      }
      g.fillStyle(0x3d0080, 1); g.fillTriangle(15, 0, 2, 60, 28, 60);
      g.fillStyle(0x7b2fff, 1); g.fillTriangle(15, 2, 5, 58, 25, 58);
      g.fillStyle(0xaa60ff, 0.5); g.fillTriangle(15, 6, 9, 30, 21, 30);
      g.fillStyle(0xffffff, 0.3); g.fillTriangle(13, 8, 8, 22, 18, 22);
    });

    this.makeTexture('crystal_lit', 30, 60, (g) => {
      for (let i = 5; i >= 1; i--) {
        g.fillStyle(0xff8c00, 0.07 * i); g.fillTriangle(15, -i * 6, -i * 4, 65, 30 + i * 4, 65);
      }
      g.fillStyle(0xcc4400, 1); g.fillTriangle(15, 0, 2, 60, 28, 60);
      g.fillStyle(0xff6b00, 1); g.fillTriangle(15, 2, 5, 58, 25, 58);
      g.fillStyle(0xffa500, 0.6); g.fillTriangle(15, 6, 9, 30, 21, 30);
      g.fillStyle(0xffd700, 0.7); g.fillTriangle(13, 8, 8, 22, 18, 22);
      g.fillStyle(0xffffff, 0.5); g.fillTriangle(14, 10, 10, 20, 18, 20);
    });
  }

  // ── Helpers ──────────────────────────────────────────────────
  private makeTexture(key: string, w: number, h: number, draw: (g: Phaser.GameObjects.Graphics) => void) {
    const g = this.make.graphics({ x: 0, y: 0 });
    draw(g);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private starPath(g: Phaser.GameObjects.Graphics, cx: number, cy: number, points: number, outer: number, inner: number) {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < points * 2; i++) {
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const r = i % 2 === 0 ? outer : inner;
      pts.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
    }
    g.fillPoints(pts, true);
  }
}
